import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import config from '../../config';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/contextHelpers';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import mapPin from '../../assets/map-pin.svg';

import {
  Page,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  AvatarXLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSingleColumn,
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  IconSocialMediaTwitter,
  ExternalLink,
  Modal,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;

export class ProfilePageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // keep track of which reviews tab to show in desktop viewport
      showReviewsType: REVIEW_TYPE_OF_PROVIDER,
      isPoliciesOpen: false,
    };

    this.showOfProviderReviews = this.showOfProviderReviews.bind(this);
    this.showOfCustomerReviews = this.showOfCustomerReviews.bind(this);
  }

  showOfProviderReviews() {
    this.setState({
      showReviewsType: REVIEW_TYPE_OF_PROVIDER,
    });
  }

  showOfCustomerReviews() {
    this.setState({
      showReviewsType: REVIEW_TYPE_OF_CUSTOMER,
    });
  }

  render() {
    const {
      scrollingDisabled,
      currentUser,
      user,
      userShowError,
      queryListingsError,
      listings,
      reviews,
      queryReviewsError,
      viewport,
      intl,
      onManageDisableScrolling,
    } = this.props;
    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const profileUser = ensureUser(user);
    const isCurrentUser =
      ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
    const displayName = profileUser.attributes.profile.displayName;
    const bio = profileUser.attributes.profile.bio;
    const hasBio = !!bio;
    const hasListings = listings.length > 0;
    const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;

    // Shop information
    const userShopName = profileUser.attributes.profile.publicData?.shopName ?
                  profileUser.attributes.profile.publicData.shopName
                          : profileUser?.attributes.profile.displayName;

    const shopDescription = user?.attributes.profile.publicData?.shopDescription;
    const hasShopDescription = shopDescription && shopDescription !== '';
    const city = user?.attributes.profile.publicData?.city;
    const country = user?.attributes.profile.publicData?.country;
    const state = user?.attributes.profile.publicData?.state;
    const facebook = user?.attributes.profile.publicData?.shopFacebook;
    const twitter = user?.attributes.profile.publicData?.shopTwitter;
    const instagram = user?.attributes.profile.publicData?.shopInstagram;
    const shopPolicies = user?.attributes.profile.publicData?.shopPolicies;

    const editLinkMobile = isCurrentUser ? (
      <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
        <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
      </NamedLink>
    ) : null;
    const editLinkDesktop = isCurrentUser ? (
      <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
        <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
      </NamedLink>
    ) : null;

    const editShopMobile = isCurrentUser ? (
      <NamedLink className={css.editLinkMobile} name="ShopDetailsPage">
        <FormattedMessage id="ProfilePage.editShopLinkMobile" />
      </NamedLink>
    ) : null;
    const editShopDesktop = isCurrentUser ? (
      <NamedLink className={css.editLinkDesktop} name="ShopDetailsPage">
        <FormattedMessage id="ProfilePage.editShopLinkDesktop" />
      </NamedLink>
    ) : null;

    const listingsContainerClasses = classNames(
      css.listingsContainer,
      {
        [css.withBioMissingAbove]: !hasBio,
      },
      css.content
    );

    const reviewsError = (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
      </p>
    );

    const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);

    const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

    const mobileReviews = (
      <div className={css.mobileReviews}>
        {
          <Fragment>
            <h2 className={css.mobileReviewsTitle}>Reviews</h2>
            {queryReviewsError ? reviewsError : null}
            <Reviews reviews={reviewsOfProvider} />
          </Fragment>
        }

        <h2 className={css.mobileReviewsTitle}>Reviews</h2>
        {queryReviewsError ? reviewsError : null}
        <Reviews reviews={reviewsOfCustomer} />
      </div>
    );

    const sellerReviewsTab = {
      text: (
        <h3 className={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsOfProviderTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </h3>
      ),
      selected: this.state.showReviewsType === REVIEW_TYPE_OF_PROVIDER,
      onClick: this.showOfProviderReviews,
    };

    const buyerReviewsTab = {
      text: (
        <h3 className={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsOfCustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </h3>
      ),
      selected: this.state.showReviewsType === REVIEW_TYPE_OF_CUSTOMER,
      onClick: this.showOfCustomerReviews,
    };

    // const desktopReviewTabs = isSeller ? [sellerReviewsTab] : [buyerReviewsTab];

    const desktopReviews = (
      <div className={classNames(css.desktopReviews, css.content)}>
        <div className={css.desktopReviewsWrapper}>
          {/* <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} /> */}

          {queryReviewsError ? reviewsError : null}
          <h3 className={css.desktopReviewsTitleHeader}>Reviews</h3>
          {<Reviews reviews={reviewsOfProvider} />}
        </div>
      </div>
    );

    const mainContent = (
      <div>
        <div className={css.headerBackgorund}>
          <div className={css.content}>
            <div className={css.shopHeading}>
              <div className={css.shopAvatar}>
                <AvatarXLarge className={css.avatar} user={user} disableProfileLink />
              </div>
              <div className={css.bubbleContainer}>
                <div className={css.shopDescriptionBubble}>
                  <h2 className={css.shopNameHeading}>{displayName}</h2>
                  {hasBio ? <p>{bio}</p> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={css.content}>{isMobileLayout ? mobileReviews : desktopReviews}</div>
      </div>
    );

    const mainSellerContent = (
      <div>
        {
          <Fragment>
            <div className={css.headerBackgorund}>
              <div className={css.content}>
                <div className={css.shopHeading}>
                  <div className={css.shopAvatar}>
                    <AvatarXLarge className={css.avatar} user={user} disableProfileLink />
                  </div>
                  <div className={css.bubbleContainer}>
                    <div className={css.shopDescriptionBubble}>
                      <h2 className={css.shopNameHeading}>{userShopName}</h2>
                      {hasShopDescription && <p>{shopDescription}</p>}
                    </div>
                    <div className={css.shopAddress}>
                      <img src={mapPin}></img>
                      <span>
                        {city}, {state} {country}
                      </span>
                    </div>
                  </div>
                  <div className={css.sideNav}>
                    <div className={css.socials}>
                      {facebook ? (
                        <ExternalLink href={facebook} className={css.withPadding}>
                          <IconSocialMediaFacebook></IconSocialMediaFacebook>
                        </ExternalLink>
                      ) : null}
                      {twitter ? (
                        <ExternalLink href={twitter} className={css.withPadding}>
                          {' '}
                          <IconSocialMediaTwitter></IconSocialMediaTwitter>
                        </ExternalLink>
                      ) : null}
                      {instagram ? (
                        <ExternalLink href={instagram} className={css.withPadding}>
                          <IconSocialMediaInstagram></IconSocialMediaInstagram>
                        </ExternalLink>
                      ) : null}
                    </div>
                    {shopPolicies ? (
                      <div
                        onClick={() => {
                          this.setState({ isPoliciesOpen: true });
                        }}
                        className={css.shopPoliciesLink}
                      >
                        Show shop policies
                      </div>
                    ) : null}
                  </div>
                  <div className={css.editLinks}>{editShopDesktop}</div>
                </div>
              </div>
            </div>
            <Modal
              onManageDisableScrolling={onManageDisableScrolling}
              id="ShopPoliciesProfileModal"
              isOpen={this.state.isPoliciesOpen}
              onClose={() => this.setState({ isPoliciesOpen: false })}
            >
              <div className={css.shopPoliciesContent}>
                <h2>
                  <FormattedMessage id="ShopDetails.PoliciesTitle" />
                </h2>
                <p>{shopPolicies}</p>
              </div>
            </Modal>
          </Fragment>
        }

        {hasListings ? (
          <div className={listingsContainerClasses}>
            <h2 className={css.listingsTitle}>
              <FormattedMessage
                id="ProfilePage.listingsTitle"
                values={{ count: listings.length }}
              />
            </h2>
            <div className={css.listings}>
              {listings.map(l => (
                <div className={css.listing} key={l.id.uuid}>
                  <ListingCard className={css.cardRoot} listing={l} showAuthorInfo={true} />
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {isMobileLayout ? mobileReviews : desktopReviews}
      </div>
    );

    let content;

    if (userShowError && userShowError.status === 404) {
      return <NotFoundPage />;
    } else if (userShowError || queryListingsError) {
      content = (
        <p className={css.error}>
          <FormattedMessage id="ProfilePage.loadingDataFailed" />
        </p>
      );
    } else {
      content = mainSellerContent;
    }

    const schemaTitle = intl.formatMessage(
      {
        id: 'ProfilePage.schemaTitle',
      },
      {
        name: displayName,
        siteTitle: config.siteTitle,
      }
    );

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        title={schemaTitle}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'ProfilePage',
          name: schemaTitle,
        }}
      >
        <LayoutSingleColumn className={css.pageRoot}>
          <LayoutWrapperTopbar>
            <TopbarContainer currentPage="ProfilePage" />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain>{content}</LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

const { bool, arrayOf, number, shape } = PropTypes;

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ProfilePage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
